import React, { useEffect, useRef, useState } from "react";
import CommonTable from "../../../components/Table/Table";
import {
    getAllBanksApi,
    createBankApi,
    updateBankApi,
    deleteBankApi,
    updateBankStatusApi 
} from "../../../services/BankMaster/bankMaster.service";
import { Pencil, Eye, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import "./bankList.css";

type Bank = {
    id: number;
    name: string;
    mobileNumber: string;
    bankName: string;
    accountNumber: string;
    ifscCode: string;
    status: "ACTIVE" | "INACTIVE";
};

const initialForm: Bank = {
    id: 0,
    name: "",
    mobileNumber: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    status: "ACTIVE",
};

const BankList: React.FC = () => {
    const [banks, setBanks] = useState<any[]>([]);
    const [search, setSearch] = useState("");
    const [form, setForm] = useState<Bank>(initialForm);
    const [showModal, setShowModal] = useState(false);
    const [viewData, setViewData] = useState<Bank | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const calledOnce = useRef(false);
    const itemsPerPage = 10;

    const loadBanks = async () => {
        try {
            const res = await getAllBanksApi();
            const data = res?.data || [];

            const mapped = data.map((b: any, index: number) => ({
                ...b,
                serial: index + 1,
            }));

            setBanks(mapped);
        } catch {
            setBanks([]);
        }
    };

    const handleStatusToggle = async (row: Bank) => {
    try {
        const newStatus = row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

        await updateBankStatusApi(row.id, newStatus);

        toast.success(`Status changed to ${newStatus}`);

        // UI update without reload 🔥
        setBanks((prev) =>
            prev.map((b) =>
                b.id === row.id ? { ...b, status: newStatus } : b
            )
        );
    } catch {
        toast.error("Status update failed");
    }
};

    useEffect(() => {
        if (calledOnce.current) return;
        calledOnce.current = true;
        loadBanks();
    }, []);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            if (editId) {
                await updateBankApi(editId, form);
                toast.success("Updated Successfully");
            } else {
                await createBankApi(form);
                toast.success("Created Successfully");
            }

            setShowModal(false);
            setForm(initialForm);
            setEditId(null);
            loadBanks();
        } catch {
            toast.error("Something went wrong");
        }
    };

    const handleEdit = (row: Bank) => {
        setForm(row);
        setEditId(row.id);
        setShowModal(true);
    };

    const handleView = (row: Bank) => {
        setViewData(row);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteBankApi(deleteId);
            setBanks((prev) => prev.filter((b) => b.id !== deleteId));
            setDeleteId(null);
            toast.success("Deleted Successfully");
        } catch {
            toast.error("Delete Failed");
        }
    };

    const filtered = banks.filter((b) =>
        b.name.toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;

    const paginatedData = filtered
        .slice(startIndex, startIndex + itemsPerPage)
        .map((item, index) => ({
            ...item,
            serial: startIndex + index + 1,
        }));

    const columns = [
        { key: "serial", label: "S.NO" },
        { key: "name", label: "Name" },
        { key: "mobileNumber", label: "Mobile" },
        { key: "bankName", label: "Bank Name" },
        { key: "accountNumber", label: "Account" },
        { key: "ifscCode", label: "IFSC" },
       {
    key: "status",
    label: "Status",
    render: (value: string, row: Bank) => (
        <span
            className={`status-pill ${value.toLowerCase()}`}
            style={{ cursor: "pointer" }}
            onClick={() => handleStatusToggle(row)}
        >
            {value}
        </span>
    ),
}
    ];

    return (
        <div className="category-container">
            <div className="header-bar">
                <input
                    type="text"
                    placeholder="Search Bank..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="search-bar"
                />
                <button
                    className="add-btn"
                    onClick={() => {
                        setForm(initialForm);   // reset form
                        setEditId(null);        // 🔥 IMPORTANT FIX
                        setShowModal(true);
                    }}
                >
                    Add Bank
                </button>
            </div>

            <CommonTable
                columns={columns}
                data={paginatedData}
                tableClassName="compact-table"
                actions={(row: Bank) => (
                    <div className="action-icons">
                        <button className="icon-btn" onClick={() => handleView(row)}>
                            <Eye size={16} />
                        </button>

                        <button className="icon-btn" onClick={() => handleEdit(row)}>
                            <Pencil size={16} />
                        </button>

                        <button
                            className="icon-btn delete"
                            onClick={() => setDeleteId(row.id)}
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                )}
            />

            {/* Pagination */}
            <div className="pagination">
                <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>

                <span>
                    Page {currentPage} of {totalPages}
                </span>

                <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{editId ? "Edit Bank" : "Add Bank"}</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <input name="name" value={form.name} onChange={handleChange} placeholder="Name" />
                        <input name="mobileNumber" value={form.mobileNumber} onChange={handleChange} placeholder="Mobile" />
                        <input name="bankName" value={form.bankName} onChange={handleChange} placeholder="Bank Name" />
                        <input name="accountNumber" value={form.accountNumber} onChange={handleChange} placeholder="Account" />
                        <input name="ifscCode" value={form.ifscCode} onChange={handleChange} placeholder="IFSC" />

                        <select name="status" value={form.status} onChange={handleChange}>
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                        </select>

                        <button className="add-btn" onClick={handleSubmit}>
                            {editId ? "Update" : "Create"}
                        </button>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {viewData && (
                <div className="modal-overlay" onClick={() => setViewData(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Bank Details</h3>
                            <button className="close-btn" onClick={() => setViewData(null)}>
                                <X size={18} />
                            </button>
                        </div>

                        <p><b>Name:</b> {viewData.name}</p>
                        <p><b>Mobile:</b> {viewData.mobileNumber}</p>
                        <p><b>Bank:</b> {viewData.bankName}</p>
                        <p><b>Account:</b> {viewData.accountNumber}</p>
                        <p><b>IFSC:</b> {viewData.ifscCode}</p>
                        <p><b>Status:</b> {viewData.status}</p>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {deleteId && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="close-btn" onClick={() => setDeleteId(null)}>
                                <X size={18} />
                            </button>
                        </div>

                        <button className="add-btn delete" onClick={confirmDelete}>
                            Yes
                        </button>
                        <button onClick={() => setDeleteId(null)}>No</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankList;